import React, { useState, useEffect, useMemo } from "react";
import {
  EditButton,
  List,
  ShowButton,
  useDataGrid,
  DeleteButton,
} from "@refinedev/mui";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  BaseRecord,
  getDefaultFilter,
  HttpError,
  IResourceComponentsProps,
  useTranslate,
  useList,
  useNavigation,
} from "@refinedev/core";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";
import { Autocomplete } from "@mui/material";
import { format } from "date-fns";

export interface IDiscount {
  id: number;
  name: string;
  type: "percentage" | "fixed_amount";
  percentage?: number;
  amount?: number;
  currency?: string;
  timesRedeemed: number;
  maxRedemptions?: number;
  redeemBy?: Date;
  active: boolean;
  description?: string;
}

export interface IDiscountFilterVariables {
  q?: string;
  name?: string;
  active?: boolean;
  type?: string;
}

export const DiscountList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const { push } = useNavigation();

  // Add state for clipboard notification
  const [copySnackbar, setCopySnackbar] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });

  // Function to handle copy to clipboard
  const handleCopyName = (name: string) => {
    navigator.clipboard.writeText(name).then(
      () => {
        setCopySnackbar({
          open: true,
          message: `Discount "${name}" copied to clipboard!`,
        });
      },
      (err) => {
        console.error("Could not copy text: ", err);
        setCopySnackbar({
          open: true,
          message: "Failed to copy discount name.",
        });
      }
    );
  };

  // Handle closing the snackbar
  const handleCloseSnackbar = () => {
    setCopySnackbar({
      ...copySnackbar,
      open: false,
    });
  };

  // État de chargement pour le filtrage
  const [isFiltering, setIsFiltering] = useState(false);
  // État pour stocker tous les éléments et éléments filtrés
  const [filteredItems, setFilteredItems] = useState<IDiscount[]>([]);
  // État pour stocker les anciennes lignes (pour éviter le flash de toutes les lignes)
  const [lastValidRows, setLastValidRows] = useState<IDiscount[]>([]);
  // État pour les critères de filtrage actifs
  const [activeFilters, setActiveFilters] = useState<IDiscountFilterVariables>(
    {}
  );

  // Récupération de tous les discounts avec useList au lieu de refetch
  const { data: discountsData } = useList<IDiscount>({
    resource: "discounts",
  });

  const allItems = useMemo(() => discountsData?.data || [], [discountsData]);

  const { dataGridProps, search, filters } = useDataGrid<
    IDiscount,
    HttpError,
    IDiscountFilterVariables
  >({
    initialPageSize: 10,
    onSearch: (params) => {
      // Activer l'état de filtrage
      setIsFiltering(true);
      // Stockez les filtres actuels
      setActiveFilters(params);

      // Sauvegarder l'état actuel des lignes
      if (dataGridProps.rows && dataGridProps.rows.length > 0) {
        setLastValidRows(dataGridProps.rows as IDiscount[]);
      }

      // Filtrage côté client, retourner un tableau vide pour les filtres API
      return [];
    },
    pagination: { mode: "server" },
  });

  // État pour la pagination locale
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  // Effet pour filtrer les éléments selon les critères
  useEffect(() => {
    if (!allItems.length) return;

    // Fonction pour filtrer les éléments selon les critères actifs
    const performFiltering = async () => {
      let result = [...allItems];
      const { name, active, type } = activeFilters;

      // Filtre par nom s'il est spécifié
      if (name && name !== "") {
        result = result.filter((item) =>
          item.name.toLowerCase().includes(name.toLowerCase())
        );
      }

      // Filtre par statut actif s'il est spécifié
      if (active !== undefined && active !== null) {
        result = result.filter((item) => item.active === Boolean(active));
      }

      // Filtre par type s'il est spécifié
      if (type && type !== "") {
        result = result.filter((item) => item.type === type);
      }

      // Utiliser setTimeout pour éviter le flash des résultats
      setTimeout(() => {
        setFilteredItems(result);
        setIsFiltering(false);
      }, 50);
    };

    performFiltering();
  }, [activeFilters, allItems]);

  // Effet pour mettre à jour lastValidRows séparément
  useEffect(() => {
    if (!isFiltering && dataGridProps.rows && dataGridProps.rows.length > 0) {
      setLastValidRows(dataGridProps.rows as IDiscount[]);
    }
  }, [dataGridProps.rows, isFiltering]);

  // Override des lignes du DataGrid avec nos données filtrées mais en préservant la pagination
  const getFilteredRows = () => {
    // Pendant le filtrage, afficher les dernières lignes valides pour éviter un flash
    if (isFiltering && lastValidRows.length > 0) {
      return lastValidRows;
    }

    // Si aucun filtre actif, utiliser les données originales du dataGridProps pour préserver pagination
    if (
      (!activeFilters.name || activeFilters.name === "") &&
      (activeFilters.active === undefined || activeFilters.active === null) &&
      (!activeFilters.type || activeFilters.type === "")
    ) {
      return dataGridProps.rows || [];
    }

    // Si filtrage terminé, appliquer la pagination manuellement aux résultats
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return filteredItems.slice(start, end);
  };

  const columns = React.useMemo<GridColDef<IDiscount>[]>(
    () => [
      {
        field: "active",
        headerName: "",
        width: 40,
        minWidth: 40,
        flex: 0,
        filterable: false,
        sortable: false,
        renderCell: function render({ value }) {
          return (
            <Tooltip
              title={
                value
                  ? translate("active.active", "Active")
                  : translate("active.inactive", "Inactive")
              }
              arrow
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  cursor: "help",
                }}
              >
                {value ? "🟢" : "🔴"}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "name",
        flex: 1,
        headerName: translate("discount.fields.name", "Name"),
        minWidth: 200,
        renderCell: function render({ value }) {
          const isDark = theme.palette.mode === 'dark';
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: "1rem",
                  fontWeight: 500,
                  display: "inline-block",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  backgroundColor: isDark ? theme.palette.grey[700] : "#f5f5f5",
                  color: isDark ? theme.palette.common.white : theme.palette.common.black,
                  whiteSpace: "nowrap",
                  cursor: "text",
                  maxWidth: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {value}
              </Box>
              <Tooltip title="Copy discount name">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row selection
                    handleCopyName(value);
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "discount",
        flex: 1,
        headerName: translate("discount.fields.discount", "Discount"),
        minWidth: 120,
        renderCell: function render({ row }) {
          const isDark = theme.palette.mode === 'dark';
          const discountText = row.type === 'percentage'
            ? `${row.percentage}%`
            : `${row.amount} ${row.currency?.toUpperCase() || ''}`;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: "1.1rem",
                color: isDark ? theme.palette.success.light : theme.palette.success.dark,
                backgroundColor: isDark ? theme.palette.success.dark + '20' : theme.palette.success.light + '20',
                padding: "4px 8px",
                borderRadius: "4px",
                border: `1px solid ${isDark ? theme.palette.success.dark : theme.palette.success.main}`,
              }}
            >
              {discountText}
            </Box>
          );
        },
      },
      {
        field: "usage",
        flex: 1,
        headerName: translate("discount.fields.usage", "Usage"),
        minWidth: 100,
        renderCell: function render({ row }) {
          const used = row.timesRedeemed || 0;
          const max = row.maxRedemptions;
          const usageText = max ? `${used}/${max}` : `${used}/∞`;

          return (
            <Box
              sx={{
                fontFamily: "'Courier New', Courier, monospace",
                fontWeight: "bold",
              }}
            >
              {usageText}
            </Box>
          );
        },
      },
      {
        field: "redeemBy",
        flex: 1,
        headerName: translate("discount.fields.expires", "Expires"),
        minWidth: 120,
        renderCell: function render({ value }) {
          try {
            if (!value) return "Never";
            return format(new Date(value), "dd/MM/yyyy");
          } catch (error) {
            console.error("Error formatting date:", error, value);
            return "Invalid date";
          }
        },
      },
      {
        field: "actions",
        headerName: translate("discount.fields.actions", "Actions"),
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <ShowButton hideText recordItemId={row.id} />
              <DeleteButton hideText recordItemId={row.id} />
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 120,
      },
    ],
    [translate, theme]
  );

  const { register, handleSubmit, control, reset } = useForm<
    BaseRecord,
    HttpError,
    IDiscountFilterVariables
  >({
    defaultValues: {
      q: getDefaultFilter("q", filters, "eq") || "",
      name: getDefaultFilter("name", filters, "contains") || "",
      active: getDefaultFilter("active", filters, "eq"),
      type: getDefaultFilter("type", filters, "eq") || "",
    },
  });

  // Add reset function to clear filters
  const handleClearFilters = () => {
    reset({
      q: "",
      name: "",
      active: undefined,
      type: "",
    });
    search({ q: "", name: "", active: undefined, type: "" });
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={3}>
          <Card>
            <CardHeader title={translate("pages.filter", "Filters")} />
            <CardContent sx={{ pt: 0 }}>
              <Box
                component="form"
                sx={{ display: "flex", flexDirection: "column" }}
                autoComplete="off"
                onSubmit={handleSubmit(search)}
              >
                <TextField
                  {...register("name")}
                  label={translate("pages.search", "Search")}
                  placeholder={translate(
                    "pages.searchByName",
                    "Search by name"
                  )}
                  margin="normal"
                  fullWidth
                  autoFocus
                  size="small"
                />
                <Controller
                  control={control}
                  name="active"
                  render={({ field }) => (
                    <Autocomplete
                      options={[true, false, null]}
                      {...field}
                      onChange={(_, value) => {
                        field.onChange(value);
                      }}
                      getOptionLabel={(item) => {
                        if (item === true) return "Active";
                        if (item === false) return "Inactive";
                        return "All";
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate("active.title", "Active Status")}
                          placeholder={translate(
                            "active.select",
                            "Select status"
                          )}
                          margin="normal"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <Autocomplete
                      options={["percentage", "fixed_amount", ""]}
                      {...field}
                      onChange={(_, value) => {
                        field.onChange(value || "");
                      }}
                      getOptionLabel={(item) => {
                        if (item === "percentage") return "Percentage";
                        if (item === "fixed_amount") return "Fixed Amount";
                        return "All Types";
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate("discount.type", "Discount Type")}
                          placeholder={translate(
                            "discount.selectType",
                            "Select type"
                          )}
                          margin="normal"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    />
                  )}
                />
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button type="submit" variant="contained">
                    {translate("buttons.search", "Search")}
                  </Button>
                  <Button variant="outlined" onClick={handleClearFilters}>
                    {translate("buttons.clear", "Clear")}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={9}>
          <List>
            <DataGrid
              {...dataGridProps}
              rows={getFilteredRows()}
              rowCount={
                // Si filtrage actif, utiliser le nombre total d'éléments filtrés
                (!activeFilters.name || activeFilters.name === "") &&
                (activeFilters.active === undefined ||
                  activeFilters.active === null) &&
                (!activeFilters.type || activeFilters.type === "")
                  ? dataGridProps.rowCount
                  : filteredItems.length
              }
              columns={columns}
              autoHeight
              loading={isFiltering} // Afficher un indicateur de chargement pendant le filtrage
              disableRowSelectionOnClick
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10]}
              paginationMode="server"
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
            />
          </List>
        </Grid>
      </Grid>
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message={copySnackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};
